import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { get, post, patch, del } from '../utils/api';
import { Container, Table, Button, Modal, Form, Row, Col, InputGroup, Pagination, Badge, Tab, Nav, Card } from 'react-bootstrap';
import { 
  FaSearch, FaTimesCircle, FaPlus, FaTrash, FaEdit, 
  FaFileInvoiceDollar, FaCheckCircle, FaExclamationCircle, 
  FaReceipt, FaCoins, FaEye, FaHandHoldingUsd, FaFilePdf,
  FaWhatsapp, FaPrint, FaClipboardList, FaBoxes, FaTruckLoading,
  FaExclamationTriangle, FaRedo, FaShareAlt, FaCheck, FaTimes,
  FaArrowRight, FaClock, FaTags, FaInfoCircle, FaPaperPlane
} from 'react-icons/fa';
import { useToast } from './ToastContext';
import ProgressDialog from './ProgressDialog';
import './PurchaseManagement.css';
import config, { CURRENCY_SYMBOL } from '../config';
import _ from 'lodash';
import NoDataCard from './NoDataCard';

const PurchaseManagement = () => {
  const { showToast } = useToast();
  
  // Navigation Tabs: 'po' (Purchase Orders), 'grn' (Goods Receipts/Invoices), 'lowstock' (Auto-Requisition)
  const [activeTab, setActiveTab] = useState('po');

  // Master Lists
  const [supplierList, setSupplierList] = useState([]);
  const [medicineList, setMedicineList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Purchase Orders (PO) State ---
  const [poList, setPoList] = useState([]);
  const [poStatusFilter, setPoStatusFilter] = useState('all');
  const [poSearch, setPoSearch] = useState('');
  const [poPage, setPoPage] = useState(1);
  const [poTotalPages, setPoTotalPages] = useState(1);
  const [loadingPo, setLoadingPo] = useState(false);

  // PO Modal State
  const [showPoModal, setShowPoModal] = useState(false);
  const [poForm, setPoForm] = useState({
    supplier_id: '',
    expected_delivery_date: '',
    notes: '',
    status: 'ordered',
    items: [
      { medicine_id: '', requested_quantity: 1, unit_name: 'box', conversion_factor: 100 }
    ]
  });
  const [submittingPo, setSubmittingPo] = useState(false);

  // PO Details & Print/PDF Modal State
  const [showPoDetailsModal, setShowPoDetailsModal] = useState(false);
  const [selectedPoDetails, setSelectedPoDetails] = useState(null);
  const [loadingPoDetails, setLoadingPoDetails] = useState(false);

  // GRN (Goods Receipt Verification) Modal State
  const [showGrnModal, setShowGrnModal] = useState(false);
  const [grnPo, setGrnPo] = useState(null);
  const [grnForm, setGrnForm] = useState({
    invoice_number: '',
    items: []
  });
  const [submittingGrn, setSubmittingGrn] = useState(false);

  // --- Low Stock Suggestions State ---
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loadingLowStock, setLoadingLowStock] = useState(false);

  // --- Existing Goods Receipts (Purchases Invoices) State ---
  const [purchaseList, setPurchaseList] = useState([]);
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [purchasePage, setPurchasePage] = useState(1);
  const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loadingInvoiceDetails, setLoadingInvoiceDetails] = useState(false);

  // --- Payment Modal State ---
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPurchaseForPayment, setSelectedPurchaseForPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    transaction_id: '',
    notes: ''
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  // --- Tax & GST Reports State ---
  const [taxSummary, setTaxSummary] = useState(null);
  const [loadingTax, setLoadingTax] = useState(false);
  const [taxDateRange, setTaxDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const fetchTaxSummary = useCallback(async () => {
    setLoadingTax(true);
    try {
      let url = '/tax/summary';
      const params = [];
      if (taxDateRange.startDate) params.push(`startDate=${taxDateRange.startDate}`);
      if (taxDateRange.endDate) params.push(`endDate=${taxDateRange.endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const res = await get(url);
      if (res.success) {
        setTaxSummary(res.data);
      } else {
        showToast(res.message || 'Failed to load Tax Summary', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error loading Tax Summary', 'danger');
    } finally {
      setLoadingTax(false);
    }
  }, [taxDateRange, showToast]);

  useEffect(() => {
    if (activeTab === 'tax') {
      fetchTaxSummary();
    }
  }, [activeTab, fetchTaxSummary]);


  // Load Initial Suppliers & Medicines
  const loadMasterData = useCallback(async () => {
    setLoading(true);
    try {
      const [suppliersRes, medicinesRes] = await Promise.all([
        get('/suppliers'),
        get('/medicines?limit=10000')
      ]);

      if (suppliersRes.success) {
        setSupplierList(suppliersRes.data || []);
      }
      if (medicinesRes.success) {
        setMedicineList(medicinesRes.data || []);
      }
    } catch (err) {
      showToast(err.message || 'Failed to load master supplier/medicine list', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Fetch POs
  const fetchPurchaseOrders = useCallback(async () => {
    setLoadingPo(true);
    try {
      let url = `/purchase-orders?page=${poPage}&limit=10`;
      if (poStatusFilter !== 'all') url += `&status=${poStatusFilter}`;
      if (poSearch) url += `&search=${encodeURIComponent(poSearch)}`;

      const res = await get(url);
      if (res.success) {
        setPoList(res.data || []);
        if (res.pagination) {
          setPoTotalPages(res.pagination.total_pages || 1);
        }
      }
    } catch (err) {
      console.error('Fetch PO error:', err);
    } finally {
      setLoadingPo(false);
    }
  }, [poPage, poStatusFilter, poSearch]);

  // Fetch Existing Purchases Invoices (GRN List)
  const fetchPurchases = useCallback(async () => {
    try {
      const res = await get('/purchases?limit=10000');
      if (res.success) {
        setPurchaseList(res.data || []);
      }
    } catch (err) {
      console.error('Fetch Purchases error:', err);
    }
  }, []);

  // Fetch Low Stock Suggestions
  const fetchLowStockSuggestions = useCallback(async () => {
    setLoadingLowStock(true);
    try {
      const res = await get('/purchase-orders/low-stock-suggestions');
      if (res.success) {
        setLowStockItems(res.data || []);
      }
    } catch (err) {
      console.error('Fetch Low Stock error:', err);
    } finally {
      setLoadingLowStock(false);
    }
  }, []);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  useEffect(() => {
    if (activeTab === 'po') {
      fetchPurchaseOrders();
    } else if (activeTab === 'grn') {
      fetchPurchases();
    } else if (activeTab === 'lowstock') {
      fetchLowStockSuggestions();
    }
  }, [activeTab, fetchPurchaseOrders, fetchPurchases, fetchLowStockSuggestions]);

  // --- Handlers for PO Creation Modal ---
  const handleOpenPoModal = (prefillItems = [], supplierId = '') => {
    let initialItems = [
      { medicine_id: '', requested_quantity: 1, unit_name: 'box', conversion_factor: 100 }
    ];

    if (prefillItems.length > 0) {
      initialItems = prefillItems.map(item => {
        const med = medicineList.find(m => m.medicine_id === item.medicine_id);
        const boxFactor = med ? (parseInt(med.box_conversion) || 100) : 100;
        return {
          medicine_id: item.medicine_id,
          requested_quantity: item.requested_quantity || 5,
          unit_name: 'box',
          conversion_factor: boxFactor
        };
      });
    }

    setPoForm({
      supplier_id: supplierId || (supplierList.length > 0 ? supplierList[0].supplier_id : ''),
      expected_delivery_date: '',
      notes: '',
      status: 'ordered',
      items: initialItems
    });
    setShowPoModal(true);
  };

  const handleAddPoItem = () => {
    setPoForm(prev => ({
      ...prev,
      items: [...prev.items, { medicine_id: '', requested_quantity: 1, unit_name: 'box', conversion_factor: 100 }]
    }));
  };

  const handleRemovePoItem = (index) => {
    setPoForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handlePoItemChange = (index, field, value) => {
    setPoForm(prev => {
      const updatedItems = [...prev.items];
      const item = { ...updatedItems[index], [field]: value };

      if (field === 'medicine_id') {
        const selectedMed = medicineList.find(m => String(m.medicine_id) === String(value));
        if (selectedMed) {
          const unit = item.unit_name || 'box';
          if (unit === 'box') {
            item.conversion_factor = parseInt(selectedMed.box_conversion) || 100;
          } else if (unit === 'strip') {
            item.conversion_factor = parseInt(selectedMed.strip_conversion) || 10;
          } else {
            item.conversion_factor = 1;
          }
        }
      }

      if (field === 'unit_name') {
        const selectedMed = medicineList.find(m => String(m.medicine_id) === String(item.medicine_id));
        if (selectedMed) {
          if (value === 'box') {
            item.conversion_factor = parseInt(selectedMed.box_conversion) || 100;
          } else if (value === 'strip') {
            item.conversion_factor = parseInt(selectedMed.strip_conversion) || 10;
          } else {
            item.conversion_factor = 1;
          }
        }
      }

      updatedItems[index] = item;
      return { ...prev, items: updatedItems };
    });
  };

  const handleSubmitPo = async (statusOverride = null) => {
    if (!poForm.items || poForm.items.length === 0) {
      showToast('Please add at least one medicine item to the Purchase Order.', 'warning');
      return;
    }

    const invalidItem = poForm.items.find(i => !i.medicine_id || !i.requested_quantity || i.requested_quantity <= 0);
    if (invalidItem) {
      showToast('Please select a valid medicine and positive requested quantity for all lines.', 'warning');
      return;
    }

    setSubmittingPo(true);
    try {
      const payload = {
        ...poForm,
        status: statusOverride || poForm.status
      };
      const res = await post('/purchase-orders', payload);
      if (res.success) {
        showToast(res.message || 'Purchase Order created successfully!', 'success');
        setShowPoModal(false);
        fetchPurchaseOrders();
      } else {
        showToast(res.message || 'Failed to create Purchase Order', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error submitting Purchase Order', 'danger');
    } finally {
      setSubmittingPo(false);
    }
  };

  // --- Handlers for PO Details & Print/PDF Modal ---
  const handleViewPoDetails = async (poId) => {
    setLoadingPoDetails(true);
    setShowPoDetailsModal(true);
    try {
      const res = await get(`/purchase-orders/${poId}`);
      if (res.success) {
        setSelectedPoDetails(res.data);
      } else {
        showToast(res.message || 'Failed to load Purchase Order details', 'danger');
        setShowPoDetailsModal(false);
      }
    } catch (err) {
      showToast(err.message || 'Error fetching PO details', 'danger');
      setShowPoDetailsModal(false);
    } finally {
      setLoadingPoDetails(false);
    }
  };

  const handlePrintPo = () => {
    const originalTitle = document.title;
    if (selectedPoDetails?.po_number) {
      document.title = `Purchase_Order_${selectedPoDetails.po_number}`;
    }
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleWhatsAppShare = (po) => {
    if (!po) return;
    const phone = (po.supplier_phone || '').replace(/[^0-9]/g, '');
    let text = `*PURCHASE ORDER: ${po.po_number}*
`;
    text += `*Store:* ${config.appName} Medical Store
`;
    text += `*Supplier:* ${po.supplier_name || 'N/A'}
`;
    text += `*Date:* ${new Date(po.order_date).toLocaleDateString()}

`;
    text += `*Requested Items List:*
`;

    (po.items || []).forEach((item, idx) => {
      text += `${idx + 1}. *${item.medicine_name}* - ${item.requested_quantity} ${item.unit_name}s
`;
    });

    if (po.notes) {
      text += `
*Instructions:* ${po.notes}
`;
    }
    text += `
Please confirm stock availability and send invoice. Thank you!`;

    const encodedText = encodeURIComponent(text);
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };

  const renderPoStatusBadge = (status) => {
    const s = (status || 'ORDERED').toUpperCase().trim();
    let badgeStyle = 'bg-primary text-white';
    let label = s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

    if (s === 'RECEIVED' || s === 'COMPLETED') {
      badgeStyle = 'bg-success text-white';
    } else if (s === 'PARTIALLY_RECEIVED' || s === 'PARTIAL') {
      badgeStyle = 'bg-warning text-dark fw-bold';
    } else if (s === 'CANCELLED') {
      badgeStyle = 'bg-danger text-white';
    } else if (s === 'PENDING') {
      badgeStyle = 'bg-info text-dark fw-bold';
    }

    return (
      <span className={`badge ${badgeStyle} px-2 py-1 ms-1`}>
        {label}
      </span>
    );
  };

  // --- Handlers for Goods Receipt Verification (GRN) ---
  const handleOpenGrnModal = async (poId) => {
    setLoadingPoDetails(true);
    try {
      const res = await get(`/purchase-orders/${poId}`);
      if (res.success) {
        const poData = res.data;
        setGrnPo(poData);
        setGrnForm({
          invoice_number: `INV-${poData.po_number}`,
          items: (poData.items || []).map(item => {
            const med = medicineList.find(m => String(m.medicine_id) === String(item.medicine_id));
            const gstRate = item.gst_rate !== undefined && item.gst_rate !== null
              ? parseFloat(item.gst_rate)
              : (med && med.gst_rate !== undefined && med.gst_rate !== null ? parseFloat(med.gst_rate) : 12);
            const reqQty = parseInt(item.requested_quantity, 10) || 0;
            const poFactor = parseInt(item.conversion_factor, 10) || (med && parseInt(med.box_conversion, 10)) || 100;
            const stripFactor = parseInt(item.strip_conversion || (med && med.strip_conversion), 10) || 10;
            const boxFactor = parseInt(item.box_conversion || (med && med.box_conversion), 10) || poFactor || 100;
            const baseUnit = item.base_unit_name || (med && med.unit_name) || 'piece';

            const totalOrderedBase = item.total_ordered_base_units !== undefined
              ? parseInt(item.total_ordered_base_units, 10)
              : (reqQty * poFactor);

            const totalRecBase = item.total_received_base_units !== undefined
              ? parseInt(item.total_received_base_units, 10)
              : ((parseInt(item.total_received_quantity, 10) || 0) * poFactor);

            const remBase = item.remaining_base_units !== undefined
              ? parseInt(item.remaining_base_units, 10)
              : Math.max(0, totalOrderedBase - totalRecBase);

            const remQtyInPoUnit = item.remaining_quantity !== undefined 
              ? parseInt(item.remaining_quantity, 10) 
              : Math.max(0, Math.ceil(remBase / poFactor));

            const alreadyRecInPoUnit = item.total_received_quantity !== undefined
              ? parseInt(item.total_received_quantity, 10)
              : Math.floor(totalRecBase / poFactor);

            return {
              po_item_id: item.po_item_id,
              medicine_id: item.medicine_id,
              medicine_name: item.medicine_name,
              hsn_code: item.hsn_code || (med && med.hsn_code) || '3004',
              gst_rate: gstRate,
              tax_rate: gstRate,

              // Baseline PO specifications
              po_requested_quantity: reqQty,
              po_unit_name: item.unit_name || 'box',
              po_conversion_factor: poFactor,
              total_ordered_base_units: totalOrderedBase,
              total_received_base_units: totalRecBase,
              remaining_base_units: remBase,
              base_unit_name: baseUnit,
              strip_conversion: stripFactor,
              box_conversion: boxFactor,

              // Active Receiving Row values
              unit_name: item.unit_name || 'box',
              conversion_factor: poFactor,
              requested_quantity: reqQty,
              total_received_quantity: alreadyRecInPoUnit,
              remaining_quantity: remQtyInPoUnit,
              received_quantity: remQtyInPoUnit, // default to remaining
              batch_number: `BATCH-${Date.now().toString().slice(-6)}`,
              expiry_date: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              purchase_price: 15.00,
              selling_price: 30.00,
              is_split: false
            };
          })
        });
        setShowGrnModal(true);
      } else {
        showToast('Failed to load Purchase Order for Goods Receipt verification.', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error preparing Goods Receipt Note', 'danger');
    } finally {
      setLoadingPoDetails(false);
    }
  };

  const handleAddSplitBatch = (idx) => {
    setGrnForm(prev => {
      const source = prev.items[idx];
      const newItem = {
        ...source,
        received_quantity: 0,
        batch_number: `BATCH-${Date.now().toString().slice(-6)}`,
        expiry_date: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        is_split: true
      };
      const updated = [...prev.items];
      updated.splice(idx + 1, 0, newItem);
      return { ...prev, items: updated };
    });
  };

  const handleRemoveSplitBatch = (idx) => {
    setGrnForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  const handleGrnItemChange = (index, field, value) => {
    setGrnForm(prev => {
      const updated = [...prev.items];
      if (field === 'gst_rate') {
        const rate = parseFloat(value) || 0;
        updated[index] = { ...updated[index], gst_rate: rate, tax_rate: rate };
      } else if (field === 'unit_name') {
        const item = updated[index];
        const newUnit = value;
        const stripFactor = parseInt(item.strip_conversion, 10) || 10;
        const boxFactor = parseInt(item.box_conversion, 10) || 100;
        
        let newFactor = 1;
        if (newUnit === 'box') {
          newFactor = boxFactor;
        } else if (newUnit === 'strip') {
          newFactor = stripFactor;
        } else {
          newFactor = 1;
        }

        const totalOrderedBase = item.total_ordered_base_units !== undefined ? item.total_ordered_base_units : (item.po_requested_quantity * item.po_conversion_factor);
        const totalReceivedBase = item.total_received_base_units !== undefined ? item.total_received_base_units : (item.total_received_quantity * item.po_conversion_factor);
        const remainingBase = item.remaining_base_units !== undefined ? item.remaining_base_units : Math.max(0, totalOrderedBase - totalReceivedBase);

        // Convert requested, already received, and remaining quantities to newly selected unit
        const newReqQty = newFactor > 0 ? Math.round(totalOrderedBase / newFactor) : totalOrderedBase;
        const newAlreadyRec = newFactor > 0 ? Math.floor(totalReceivedBase / newFactor) : totalReceivedBase;
        const newRemQty = newFactor > 0 ? Math.max(0, Math.ceil(remainingBase / newFactor)) : remainingBase;

        // Proportionally adjust purchase and selling price to the new unit
        const oldFactor = item.conversion_factor || 1;
        let newPurchasePrice = item.purchase_price;
        let newSellingPrice = item.selling_price;
        if (oldFactor > 0 && newFactor > 0 && oldFactor !== newFactor) {
          const perBasePurchase = parseFloat(item.purchase_price) / oldFactor;
          const perBaseSelling = parseFloat(item.selling_price) / oldFactor;
          newPurchasePrice = parseFloat((perBasePurchase * newFactor).toFixed(2));
          newSellingPrice = parseFloat((perBaseSelling * newFactor).toFixed(2));
        }

        updated[index] = {
          ...item,
          unit_name: newUnit,
          conversion_factor: newFactor,
          requested_quantity: newReqQty,
          total_received_quantity: newAlreadyRec,
          remaining_quantity: newRemQty,
          received_quantity: newRemQty, // automatically defaults to remaining in this new unit
          purchase_price: newPurchasePrice,
          selling_price: newSellingPrice,
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, items: updated };
    });
  };

  const handleSubmitGrn = async () => {
    if (!grnPo) return;
    const totalRecv = grnForm.items.reduce((sum, i) => sum + (parseInt(i.received_quantity, 10) || 0), 0);
    if (totalRecv <= 0) {
      showToast('Please enter a received quantity greater than 0 for at least one item.', 'warning');
      return;
    }
    setSubmittingGrn(true);
    try {
      const res = await post(`/purchase-orders/${grnPo.po_id}/verify-receipt`, grnForm);
      if (res.success) {
        showToast(res.message || 'Goods Receipt verified and stock updated!', 'success');
        setShowGrnModal(false);
        fetchPurchaseOrders();
        fetchPurchases();
      } else {
        showToast(res.message || 'Failed to verify Goods Receipt Note', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error verifying Goods Receipt Note', 'danger');
    } finally {
      setSubmittingGrn(false);
    }
  };

  // --- Handlers for Recording Supplier Payment & History ---
  const handleViewInvoiceDetails = async (purchaseId) => {
    setLoadingInvoiceDetails(true);
    setShowInvoiceDetailsModal(true);
    try {
      const res = await get(`/purchases/${purchaseId}`);
      if (res.success) {
        setInvoiceDetails(res.data);
      } else {
        showToast(res.message || 'Failed to fetch invoice details', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error loading invoice details', 'danger');
    } finally {
      setLoadingInvoiceDetails(false);
    }
  };

  const handleOpenPaymentModal = async (purchase) => {
    setSelectedPurchaseForPayment(purchase);
    const totalAmt = parseFloat(purchase.total_amount || 0);
    const totalPaid = parseFloat(purchase.total_paid || 0);
    const remDue = purchase.remaining_due !== undefined ? parseFloat(purchase.remaining_due) : Math.max(0, totalAmt - totalPaid);
    
    setPaymentForm({
      amount: remDue > 0 ? remDue.toFixed(2) : '0.00',
      payment_method: 'cash',
      transaction_id: '',
      notes: ''
    });
    setShowPaymentModal(true);

    try {
      const res = await get(`/purchases/${purchase.purchase_id}`);
      if (res.success) {
        setSelectedPurchaseForPayment(res.data);
      }
    } catch (err) {
      console.error('Error loading payment history:', err);
    }
  };

  const handleSubmitPayment = async (e) => {
    if (e) e.preventDefault();
    if (!selectedPurchaseForPayment) return;

    const amt = parseFloat(paymentForm.amount);
    if (isNaN(amt) || amt <= 0) {
      showToast('Please enter a valid payment amount greater than zero', 'warning');
      return;
    }

    setSubmittingPayment(true);
    try {
      const res = await post(`/purchases/${selectedPurchaseForPayment.purchase_id}/payments`, {
        amount: amt,
        payment_method: paymentForm.payment_method,
        transaction_id: paymentForm.transaction_id,
        notes: paymentForm.notes
      });

      if (res.success) {
        showToast(`Payment of ${CURRENCY_SYMBOL}${amt.toFixed(2)} recorded successfully!`, 'success');
        setShowPaymentModal(false);
        fetchPurchases();
      } else {
        showToast(res.message || 'Failed to record payment', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error recording payment', 'danger');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleQuickMarkPaid = async (purchase) => {
    const totalAmt = parseFloat(purchase.total_amount || 0);
    const totalPaid = parseFloat(purchase.total_paid || 0);
    const remDue = purchase.remaining_due !== undefined ? parseFloat(purchase.remaining_due) : Math.max(0, totalAmt - totalPaid);

    if (remDue <= 0) {
      showToast('This invoice is already fully paid.', 'info');
      return;
    }

    setLoading(true);
    try {
      const res = await post(`/purchases/${purchase.purchase_id}/payments`, {
        amount: remDue,
        payment_method: 'cash',
        notes: '1-Click Full Payment'
      });

      if (res.success) {
        showToast(`Invoice ${purchase.invoice_number} marked as Paid!`, 'success');
        fetchPurchases();
      } else {
        showToast(res.message || 'Failed to update payment status', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Error updating payment status', 'danger');
    } finally {
      setLoading(false);
    }
  };


  // Status Badge Helper
  const renderPOStatusBadge = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'draft':
        return <Badge bg="secondary" className="px-2 py-1"><FaClock className="me-1" /> Draft</Badge>;
      case 'ordered':
        return <Badge bg="primary" className="px-2 py-1"><FaTruckLoading className="me-1" /> Sent to Supplier</Badge>;
      case 'partially_received':
        return <Badge bg="warning" text="dark" className="px-2 py-1"><FaExclamationTriangle className="me-1" /> Partial Delivery (Shortage)</Badge>;
      case 'received_and_verified':
        return <Badge bg="success" className="px-2 py-1"><FaCheckCircle className="me-1" /> Received & Verified</Badge>;
      case 'cancelled':
        return <Badge bg="danger" className="px-2 py-1"><FaTimesCircle className="me-1" /> Cancelled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Payment Method Badge Helper
  const renderPaymentMethodBadge = (method) => {
    const m = (method || 'cash').toLowerCase().trim();
    
    switch (m) {
      case 'upi':
      case 'gpay':
      case 'phonepe':
      case 'paytm':
        return (
          <span className="badge pmt-badge-upi">
            📱 UPI / GPay
          </span>
        );
      case 'cash':
        return (
          <span className="badge pmt-badge-cash">
            💵 Cash
          </span>
        );
      case 'bank_transfer':
      case 'neft':
      case 'rtgs':
      case 'imps':
      case 'bank':
        return (
          <span className="badge pmt-badge-bank">
            🏦 Bank Transfer
          </span>
        );
      case 'card':
      case 'credit_card':
      case 'debit_card':
        return (
          <span className="badge pmt-badge-card">
            💳 Card
          </span>
        );
      case 'cheque':
        return (
          <span className="badge pmt-badge-cheque">
            📝 Cheque
          </span>
        );
      default:
        return (
          <span className="badge pmt-badge-default">
            🌐 {(method || 'Online').toUpperCase()}
          </span>
        );
    }
  };


  return (
    <Container fluid className="purchase-mgmt-page p-4">
      {/* Page Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center gap-3">
          <div className="po-master-icon">
            <FaClipboardList />
          </div>
          <div>
            <h4 className="po-title mb-0">Procurement & Purchase Orders</h4>
            <span className="po-subtitle text-muted">Real-World Pharmacy Requisition & Goods Receipt Note (GRN) Management</span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button 
            variant="primary" 
            className="po-action-btn shadow-sm"
            onClick={() => handleOpenPoModal()}
          >
            <FaPlus className="me-1" /> Create Purchase Order
          </Button>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="pills" className="po-tabs-nav mb-4 gap-2">
          <Nav.Item>
            <Nav.Link eventKey="po" className="po-tab-link">
              <FaClipboardList className="me-2" /> 1. Purchase Orders (Requisitions)
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="grn" className="po-tab-link">
              <FaReceipt className="me-2" /> 2. Goods Receipts & Invoices (GRN)
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="lowstock" className="po-tab-link">
              <FaBoxes className="me-2" /> 3. Low Stock Requisition Generator
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="tax" className="po-tab-link">
              <FaFileInvoiceDollar className="me-2" /> 4. Tax & GST Reports
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* TAB 1: PURCHASE ORDERS (REQUISITIONS) */}
          <Tab.Pane eventKey="po">
            {/* Toolbar & Filters */}
            <div className="po-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
              <Row className="g-3 align-items-center">
                <Col md={5}>
                  <InputGroup>
                    <InputGroup.Text className="bg-transparent border-0 text-muted"><FaSearch /></InputGroup.Text>
                    <Form.Control 
                      type="text" 
                      placeholder="Search PO #, supplier name, or creator..."
                      value={poSearch}
                      onChange={(e) => setPoSearch(e.target.value)}
                      className="po-search-input border-0 ps-1"
                    />
                  </InputGroup>
                </Col>
                <Col md={7} className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                  <span className="text-muted small">Filter Status:</span>
                  <div className="btn-group btn-group-sm">
                    <button 
                      className={`btn btn-outline-secondary ${poStatusFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setPoStatusFilter('all')}
                    >
                      All
                    </button>
                    <button 
                      className={`btn btn-outline-secondary ${poStatusFilter === 'ordered' ? 'active' : ''}`}
                      onClick={() => setPoStatusFilter('ordered')}
                    >
                      Sent to Supplier
                    </button>
                    <button 
                      className={`btn btn-outline-secondary ${poStatusFilter === 'partially_received' ? 'active' : ''}`}
                      onClick={() => setPoStatusFilter('partially_received')}
                    >
                      Partial / Shortage
                    </button>
                    <button 
                      className={`btn btn-outline-secondary ${poStatusFilter === 'received_and_verified' ? 'active' : ''}`}
                      onClick={() => setPoStatusFilter('received_and_verified')}
                    >
                      Verified
                    </button>
                  </div>
                </Col>
              </Row>
            </div>

            {/* PO List Table */}
            {loadingPo ? (
              <ProgressDialog show={true} message="Loading Purchase Orders..." />
            ) : poList.length === 0 ? (
              <div className="rx-no-data-box py-5 text-center rounded-4 glassmorphic-panel">
                <NoDataCard 
                  className="no-data-inline border-0 shadow-none bg-transparent"
                  title="No Purchase Orders Found"
                  message="There are no Purchase Orders matching your search criteria."
                  icon="📋"
                  actionButton={
                    <Button variant="primary" size="sm" onClick={() => handleOpenPoModal()}>
                      <FaPlus className="me-1" /> Create First Purchase Order
                    </Button>
                  }
                />
              </div>
            ) : (
              <div className="table-responsive rounded-4 glassmorphic-panel mb-4">
                <Table hover className="po-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>PO #</th>
                      <th>Supplier Details</th>
                      <th>Order Date</th>
                      <th>Expected Date</th>
                      <th>Items Count</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poList.map((po) => (
                      <tr key={po.po_id}>
                        <td>
                          <span className="fw-bold text-indigo">{po.po_number}</span>
                          {po.notes && <div className="text-muted small text-truncate" style={{ maxWidth: '160px' }}>{po.notes}</div>}
                        </td>
                        <td>
                          <div className="fw-semibold">{po.supplier_name || 'Unassigned Supplier'}</div>
                          {po.supplier_phone && <span className="text-muted small">{po.supplier_phone}</span>}
                        </td>
                        <td>{new Date(po.order_date).toLocaleDateString()}</td>
                        <td>{po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <span className="small fw-semibold">{po.total_items || 0} Medicines</span>
                            {parseInt(po.total_received_units, 10) > 0 ? (
                              <Badge 
                                bg={po.status === 'received_and_verified' ? 'success' : 'warning'} 
                                text={po.status === 'received_and_verified' ? 'light' : 'dark'}
                                className="px-2 py-1"
                              >
                                {po.total_received_units} / {po.total_requested_units || 0} units
                              </Badge>
                            ) : (
                              <Badge bg="secondary" className="px-2 py-1 text-light">
                                0 / {po.total_requested_units || 0} units
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>{renderPOStatusBadge(po.status)}</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <Button 
                              variant="outline-info" 
                              size="sm"
                              title="View & Print PO"
                              onClick={() => handleViewPoDetails(po.po_id)}
                            >
                              <FaEye /> View / PDF
                            </Button>
                            {(po.status === 'ordered' || po.status === 'partially_received' || po.status === 'draft') && (
                              <Button 
                                variant={po.status === 'partially_received' ? 'warning' : 'success'} 
                                size="sm"
                                title={po.status === 'partially_received' ? 'Receive Remaining Goods Receipt Note' : 'Verify Goods Receipt Note (GRN)'}
                                onClick={() => handleOpenGrnModal(po.po_id)}
                              >
                                <FaTruckLoading className="me-1" /> {po.status === 'partially_received' ? 'Receive Remaining' : 'Verify Receipt (GRN)'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab.Pane>

          {/* TAB 2: GOODS RECEIPTS & INVOICES (GRN) */}
          <Tab.Pane eventKey="grn">
            <div className="table-responsive rounded-4 glassmorphic-panel mb-4">
              <Table hover className="po-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Supplier</th>
                    <th>Date</th>
                    <th>Subtotal</th>
                    <th>Tax (GST)</th>
                    <th>Net Amount</th>
                    <th>Paid / Due</th>
                    <th>Payment Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseList.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">No Goods Receipts (Purchases) recorded yet.</td>
                    </tr>
                  ) : (
                    purchaseList.map((pur) => {
                      const totalAmt = parseFloat(pur.total_amount || 0);
                      const totalTax = parseFloat(pur.total_tax_amount || 0);
                      const subtotalAmt = parseFloat(pur.subtotal_amount || (totalAmt - totalTax));
                      const totalPaid = parseFloat(pur.total_paid || 0);
                      const remDue = pur.remaining_due !== undefined ? parseFloat(pur.remaining_due) : Math.max(0, totalAmt - totalPaid);
                      const isPaid = pur.payment_status === 'paid' || remDue <= 0;

                      return (
                        <tr key={pur.purchase_id}>
                          <td className="fw-bold text-indigo">{pur.invoice_number}</td>
                          <td>{pur.supplier_name || 'N/A'}</td>
                          <td>{new Date(pur.purchase_date).toLocaleDateString()}</td>
                          <td>{CURRENCY_SYMBOL}{subtotalAmt.toFixed(2)}</td>
                          <td>
                            <Badge bg="secondary" className="px-2 py-1">
                              {CURRENCY_SYMBOL}{totalTax.toFixed(2)}
                            </Badge>
                          </td>
                          <td className="fw-bold">{CURRENCY_SYMBOL}{totalAmt.toFixed(2)}</td>
                          <td>
                            <div className="small">
                              <span className="text-success fw-semibold">Paid: {CURRENCY_SYMBOL}{totalPaid.toFixed(2)}</span>
                              {remDue > 0 && (
                                <span className="text-danger ms-2 fw-semibold">Due: {CURRENCY_SYMBOL}{remDue.toFixed(2)}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge bg={isPaid ? 'success' : pur.payment_status === 'partially_paid' ? 'warning' : 'danger'}>
                              {isPaid ? 'paid' : (pur.payment_status || 'pending')}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <Button 
                                variant="outline-info" 
                                size="sm"
                                title="View Invoice & Payment Details"
                                onClick={() => handleViewInvoiceDetails(pur.purchase_id)}
                              >
                                <FaEye className="me-1" /> View / History
                              </Button>

                              {!isPaid ? (
                                <>
                                  <Button 
                                    variant="success" 
                                    size="sm"
                                    title="Mark Full Remaining Amount as Paid"
                                    onClick={() => handleQuickMarkPaid(pur)}
                                  >
                                    <FaCheckCircle className="me-1" /> Mark Paid
                                  </Button>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    title="Record Custom / Partial Payment"
                                    onClick={() => handleOpenPaymentModal(pur)}
                                  >
                                    <FaHandHoldingUsd className="me-1" /> Record Payment
                                  </Button>
                                </>
                              ) : (
                                <span className="text-muted small align-self-center ms-1">
                                  <FaCheckCircle className="text-success me-1" /> Fully Settled
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Tab.Pane>

          {/* TAB 3: LOW STOCK AUTO-REQUISITION GENERATOR */}
          <Tab.Pane eventKey="lowstock">
            {loadingLowStock ? (
              <ProgressDialog show={true} message="Analyzing Low Stock Items..." />
            ) : lowStockItems.length === 0 ? (
              <div className="rx-no-data-box py-5 text-center rounded-4 glassmorphic-panel">
                <NoDataCard 
                  className="no-data-inline border-0 shadow-none bg-transparent"
                  title="Stock Levels Healthy"
                  message="All medicines are above the minimum low-stock threshold. No urgent requisitions needed."
                  icon="✨"
                />
              </div>
            ) : (
              <div>
                <div className="alert alert-warning d-flex align-items-center justify-content-between mb-4 rounded-4">
                  <div className="d-flex align-items-center gap-2">
                    <FaExclamationTriangle className="fs-4" />
                    <div>
                      <strong>{lowStockItems.length} Medicines are below Low Stock Threshold!</strong>
                      <div className="small">Review the suggested quantities below and click to generate a prefilled Purchase Order.</div>
                    </div>
                  </div>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleOpenPoModal(lowStockItems.map(m => ({ medicine_id: m.medicine_id, requested_quantity: 5 })))}
                  >
                    <FaPlus className="me-1" /> 1-Click PO for All Low Stock Items
                  </Button>
                </div>

                <div className="table-responsive rounded-4 glassmorphic-panel">
                  <Table hover className="po-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Medicine</th>
                        <th>Generic / Mfr</th>
                        <th>Current Stock</th>
                        <th>Preferred Supplier</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStockItems.map((med) => (
                        <tr key={med.medicine_id}>
                          <td>
                            <div className="fw-bold">{med.medicine_name}</div>
                          </td>
                          <td>
                            <div>{med.generic_name || 'N/A'}</div>
                            <span className="text-muted small">{med.manufacturer || ''}</span>
                          </td>
                          <td>
                            <Badge bg="danger" className="px-2 py-1">
                              {med.current_stock} {med.unit_name || 'pieces'} remaining
                            </Badge>
                          </td>
                          <td>{med.last_supplier_name || 'Any Available Supplier'}</td>
                          <td className="text-end">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleOpenPoModal([{ medicine_id: med.medicine_id, requested_quantity: 5 }], med.last_supplier_id || '')}
                            >
                              <FaPlus className="me-1" /> Draft PO
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </Tab.Pane>

          {/* TAB 4: TAX & GST REPORTS (GSTR-3B COMPLIANCE) */}
          <Tab.Pane eventKey="tax">
            {/* Header & Date Range Filter Bar */}
            <div className="po-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
              <Row className="g-3 align-items-center justify-content-between">
                <Col md={7}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-muted small fw-bold">Period Filter:</span>
                    <InputGroup size="sm" style={{ maxWidth: '340px' }}>
                      <Form.Control
                        type="date"
                        value={taxDateRange.startDate}
                        onChange={(e) => setTaxDateRange({ ...taxDateRange, startDate: e.target.value })}
                        className="tax-date-picker"
                        title="Start Date"
                      />
                      <InputGroup.Text className="bg-transparent border-secondary text-muted">to</InputGroup.Text>
                      <Form.Control
                        type="date"
                        value={taxDateRange.endDate}
                        onChange={(e) => setTaxDateRange({ ...taxDateRange, endDate: e.target.value })}
                        className="tax-date-picker"
                        title="End Date"
                      />
                    </InputGroup>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setTaxDateRange({ startDate: '', endDate: '' })}
                      title="Clear date filter"
                    >
                      Reset
                    </Button>
                  </div>
                </Col>
                <Col md={5} className="text-md-end">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-2"
                    onClick={fetchTaxSummary}
                    disabled={loadingTax}
                  >
                    <FaRedo className={`me-1 ${loadingTax ? 'fa-spin' : ''}`} /> Refresh GST Ledger
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => window.print()}
                  >
                    <FaPrint className="me-1" /> Print Summary
                  </Button>
                </Col>
              </Row>
            </div>

            {loadingTax ? (
              <ProgressDialog show={true} message="Aggregating GST & Tax Ledger..." />
            ) : !taxSummary ? (
              <div className="rx-no-data-box py-5 text-center rounded-4 glassmorphic-panel">
                <NoDataCard 
                  className="no-data-inline border-0 shadow-none bg-transparent"
                  title="No Tax Ledger Data"
                  message="Tax records will appear once invoices or POS orders are processed."
                  icon="📊"
                />
              </div>
            ) : (
              <div>
                {/* 3 Master KPI Cards */}
                <Row className="g-3 mb-4">
                  {/* Card 1: Output Tax (Sales) */}
                  <Col md={4}>
                    <div className="tax-kpi-card output-tax-card p-3 rounded-4 glassmorphic-panel h-100">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="tax-kpi-title fw-bold text-primary">Output Tax (Sales GST)</span>
                        <Badge bg="primary" className="px-2 py-1">Customer Collected</Badge>
                      </div>
                      <div className="tax-kpi-amount fs-3 fw-bold mb-1">
                        {CURRENCY_SYMBOL}{parseFloat(taxSummary.summary?.sales?.tax_amount || 0).toFixed(2)}
                      </div>
                      <div className="small text-muted mb-2">
                        Central CGST: {CURRENCY_SYMBOL}{(parseFloat(taxSummary.summary?.sales?.tax_amount || 0) / 2).toFixed(2)} • State SGST: {CURRENCY_SYMBOL}{(parseFloat(taxSummary.summary?.sales?.tax_amount || 0) / 2).toFixed(2)}
                      </div>
                      <div className="pt-2 border-top border-secondary border-opacity-25 small text-muted d-flex justify-content-between">
                        <span>Taxable Sales Turnover:</span>
                        <strong className="tax-kpi-sub-val">{CURRENCY_SYMBOL}{parseFloat(taxSummary.summary?.sales?.subtotal || 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </Col>

                  {/* Card 2: Input Tax Credit (Purchases) */}
                  <Col md={4}>
                    <div className="tax-kpi-card itc-tax-card p-3 rounded-4 glassmorphic-panel h-100">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="tax-kpi-title fw-bold text-success">Input Tax Credit (ITC)</span>
                        <Badge bg="success" className="px-2 py-1">Supplier Paid</Badge>
                      </div>
                      <div className="tax-kpi-amount fs-3 fw-bold mb-1">
                        {CURRENCY_SYMBOL}{parseFloat(taxSummary.summary?.purchases?.tax_amount || 0).toFixed(2)}
                      </div>
                      <div className="small text-muted mb-2">
                        Central CGST: {CURRENCY_SYMBOL}{(parseFloat(taxSummary.summary?.purchases?.tax_amount || 0) / 2).toFixed(2)} • State SGST: {CURRENCY_SYMBOL}{(parseFloat(taxSummary.summary?.purchases?.tax_amount || 0) / 2).toFixed(2)}
                      </div>
                      <div className="pt-2 border-top border-secondary border-opacity-25 small text-muted d-flex justify-content-between">
                        <span>Taxable Purchases (GRN):</span>
                        <strong className="tax-kpi-sub-val">{CURRENCY_SYMBOL}{parseFloat(taxSummary.summary?.purchases?.subtotal || 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </Col>

                  {/* Card 3: Net GST Liability */}
                  <Col md={4}>
                    {(() => {
                      const netLiability = parseFloat(taxSummary.summary?.net_tax_liability || 0);
                      const isPayable = netLiability >= 0;
                      return (
                        <div className={`tax-kpi-card net-liability-card p-3 rounded-4 glassmorphic-panel h-100 ${isPayable ? 'payable-border' : 'credit-border'}`}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="tax-kpi-title fw-bold text-warning">Net Tax Liability (GSTR-3B)</span>
                            <Badge bg={isPayable ? 'danger' : 'success'} className="px-2 py-1">
                              {isPayable ? 'Tax Payable' : 'ITC Balance Carried Forward'}
                            </Badge>
                          </div>
                          <div className={`tax-kpi-amount fs-3 fw-bold mb-1 ${isPayable ? 'text-danger-bright' : 'text-success-bright'}`}>
                            {CURRENCY_SYMBOL}{Math.abs(netLiability).toFixed(2)}
                          </div>
                          <div className="small text-muted mb-2">
                            {isPayable ? 'Net cash GST payable to Government after ITC setoff' : 'Excess input credit available for future offset'}
                          </div>
                          <div className="pt-2 border-top border-secondary border-opacity-25 small text-muted d-flex justify-content-between">
                            <span>Calculation:</span>
                            <span className="tax-kpi-formula font-monospace">Output Tax - Input Tax Credit</span>
                          </div>
                        </div>
                      );
                    })()}
                  </Col>
                </Row>

                {/* Slab-wise Tables */}
                <Row className="g-4">
                  {/* Sales Slab Breakdown */}
                  <Col md={6}>
                    <div className="p-3 rounded-4 glassmorphic-panel h-100">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0 tax-slab-header d-flex align-items-center gap-2">
                          <FaCoins className="text-primary" /> Output Tax by GST Slab (Outward Supplies)
                        </h6>
                      </div>
                      <div className="table-responsive">
                        <Table hover size="sm" className="po-table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>GST Slab</th>
                              <th className="text-end">Taxable Turnover</th>
                              <th className="text-end">CGST</th>
                              <th className="text-end">SGST</th>
                              <th className="text-end">Total Tax</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(!taxSummary.sales_by_slab || taxSummary.sales_by_slab.length === 0) ? (
                              <tr>
                                <td colSpan="5" className="text-center py-3 text-muted small">No outward sales recorded in period.</td>
                              </tr>
                            ) : (
                              taxSummary.sales_by_slab.map((slab, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <Badge bg="info" className="text-dark fw-bold">{slab.tax_rate}% GST</Badge>
                                  </td>
                                  <td className="text-end">{CURRENCY_SYMBOL}{parseFloat(slab.taxable_amount || 0).toFixed(2)}</td>
                                  <td className="text-end">{CURRENCY_SYMBOL}{parseFloat(slab.cgst || 0).toFixed(2)}</td>
                                  <td className="text-end">{CURRENCY_SYMBOL}{parseFloat(slab.sgst || 0).toFixed(2)}</td>
                                  <td className="text-end fw-bold text-primary">{CURRENCY_SYMBOL}{parseFloat(slab.total_tax || 0).toFixed(2)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </Col>

                  {/* Purchases Slab Breakdown */}
                  <Col md={6}>
                    <div className="p-3 rounded-4 glassmorphic-panel h-100">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0 tax-slab-header d-flex align-items-center gap-2">
                          <FaTruckLoading className="text-success" /> Input Tax Credit by GST Slab (Inward Supplies)
                        </h6>
                      </div>
                      <div className="table-responsive">
                        <Table hover size="sm" className="po-table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>GST Slab</th>
                              <th className="text-end">Taxable Value</th>
                              <th className="text-end">CGST</th>
                              <th className="text-end">SGST</th>
                              <th className="text-end">Total ITC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(!taxSummary.purchases_by_slab || taxSummary.purchases_by_slab.length === 0) ? (
                              <tr>
                                <td colSpan="5" className="text-center py-3 text-muted small">No inward GRN purchases recorded in period.</td>
                              </tr>
                            ) : (
                              taxSummary.purchases_by_slab.map((slab, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <Badge bg="success" className="text-white fw-bold">{slab.tax_rate}% GST</Badge>
                                  </td>
                                  <td className="text-end">{CURRENCY_SYMBOL}{parseFloat(slab.taxable_amount || 0).toFixed(2)}</td>
                                  <td className="text-end">{CURRENCY_SYMBOL}{parseFloat(slab.cgst || 0).toFixed(2)}</td>
                                  <td className="text-end">{CURRENCY_SYMBOL}{parseFloat(slab.sgst || 0).toFixed(2)}</td>
                                  <td className="text-end fw-bold text-success">{CURRENCY_SYMBOL}{parseFloat(slab.total_tax || 0).toFixed(2)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* MODAL 1: CREATE PURCHASE ORDER */}
      <Modal show={showPoModal} onHide={() => setShowPoModal(false)} size="lg" centered className="purchase-modal po-modal">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaClipboardList className="me-1 text-indigo" /> Create Purchase Order (Requisition)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Select Supplier <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    value={poForm.supplier_id} 
                    onChange={(e) => setPoForm({ ...poForm, supplier_id: e.target.value })}
                  >
                    <option value="">-- Choose Supplier --</option>
                    {supplierList.map(s => (
                      <option key={s.supplier_id} value={s.supplier_id}>{s.name} ({s.phone || 'No phone'})</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Expected Delivery Date</Form.Label>
                  <Form.Control 
                    type="date"
                    value={poForm.expected_delivery_date}
                    onChange={(e) => setPoForm({ ...poForm, expected_delivery_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Item Rows */}
            <div className="po-items-section p-3 rounded-3 mb-3 glassmorphic-subpanel">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0"><FaBoxes className="me-1" /> Requested Medicines List</h6>
                <Button variant="outline-primary" size="sm" onClick={handleAddPoItem}>
                  <FaPlus className="me-1" /> Add Line Item
                </Button>
              </div>

              {poForm.items.map((item, idx) => (
                <Row key={idx} className="g-2 align-items-center mb-2 p-2 rounded po-item-row">
                  <Col md={5}>
                    <Form.Select 
                      value={item.medicine_id}
                      onChange={(e) => handlePoItemChange(idx, 'medicine_id', e.target.value)}
                      size="sm"
                    >
                      <option value="">-- Select Medicine --</option>
                      {medicineList.map(m => (
                        <option key={m.medicine_id} value={m.medicine_id}>{m.name} ({m.generic_name || 'N/A'})</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <InputGroup size="sm">
                      <Form.Control 
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={item.requested_quantity}
                        onChange={(e) => handlePoItemChange(idx, 'requested_quantity', parseInt(e.target.value, 10) || 1)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Select 
                      value={item.unit_name}
                      onChange={(e) => handlePoItemChange(idx, 'unit_name', e.target.value)}
                      size="sm"
                    >
                      <option value="box">Box (Conversion: {item.conversion_factor})</option>
                      <option value="strip">Strip</option>
                      <option value="piece">Piece</option>
                    </Form.Select>
                  </Col>
                  <Col md={1} className="text-end">
                    {poForm.items.length > 1 && (
                      <Button variant="link" className="text-danger p-0" onClick={() => handleRemovePoItem(idx)}>
                        <FaTrash />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Special Requisition Instructions / Notes</Form.Label>
              <Form.Control 
                as="textarea"
                rows={2}
                placeholder="E.g. Please verify expiry dates are at least 24 months out."
                value={poForm.notes}
                onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setShowPoModal(false)}>
            Cancel
          </Button>
          <div className="d-flex gap-2">
            <Button 
              variant="outline-primary"
              disabled={submittingPo}
              onClick={() => handleSubmitPo('draft')}
            >
              Save as Draft
            </Button>
            <Button 
              variant="primary"
              disabled={submittingPo}
              onClick={() => handleSubmitPo('ordered')}
            >
              <FaPaperPlane className="me-1" /> Submit & Send to Supplier
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* MODAL 2: PO DETAILS, PRINT PDF & WHATSAPP SHARE */}
      <Modal show={showPoDetailsModal} onHide={() => setShowPoDetailsModal(false)} size="lg" className="purchase-modal po-print-modal">
        <Modal.Header closeButton className="no-print">
          <Modal.Title className="fw-bold">
            Purchase Order Requisition: {selectedPoDetails?.po_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body id="printable-po-area" className="p-4">
          {loadingPoDetails ? (
            <ProgressDialog show={true} message="Loading PO Document..." />
          ) : selectedPoDetails && (
            <div className="po-official-document p-4 rounded-3">
              {/* Document Header */}
              <div className="po-doc-header d-flex justify-content-between align-items-start pb-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <div className="po-doc-logo-icon">💊</div>
                    <h3 className="fw-bold text-indigo mb-0">{config.appName.toUpperCase()} MEDICAL STORE</h3>
                  </div>
                  <div className="po-doc-subtitle">Official Pharmaceutical Purchase Requisition</div>
                  <div className="po-doc-meta text-muted small mt-1">
                    123 Health Avenue, Pharmacy Hub • Phone: +1-234-567-8900 • Email: orders@medicin.com
                  </div>
                  <div className="po-doc-meta text-muted small">GSTIN: 19AAACM1234F1Z5 • Drug License: DL-2024-9982</div>
                </div>
                <div className="text-end">
                  <div className="po-doc-badge mb-1">PURCHASE ORDER</div>
                  <div className="fw-bold fs-5 text-indigo">{selectedPoDetails.po_number}</div>
                  <div className="small text-muted"><strong>Date:</strong> {new Date(selectedPoDetails.order_date).toLocaleDateString()}</div>
                  <div className="small text-muted"><strong>Status:</strong> {renderPoStatusBadge(selectedPoDetails.status)}</div>
                </div>
              </div>

              {/* Vendor & Shipping Address Grid */}
              <Row className="g-3 mb-4 po-two-col-grid">
                <Col xs={6}>
                  <div className="po-details-card h-100 p-3">
                    <h6 className="po-section-title mb-2">VENDOR / SUPPLIER INFORMATION</h6>
                    <div className="fw-bold fs-6 text-indigo mb-1">{selectedPoDetails.supplier_name || 'Unassigned Supplier'}</div>
                    {selectedPoDetails.supplier_contact && <div className="small"><strong>Contact Person:</strong> {selectedPoDetails.supplier_contact}</div>}
                    {selectedPoDetails.supplier_phone && <div className="small"><strong>Phone:</strong> {selectedPoDetails.supplier_phone}</div>}
                    {selectedPoDetails.supplier_email && <div className="small"><strong>Email:</strong> {selectedPoDetails.supplier_email}</div>}
                    {selectedPoDetails.supplier_gst && <div className="small"><strong>GSTIN:</strong> {selectedPoDetails.supplier_gst}</div>}
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="po-details-card h-100 p-3">
                    <h6 className="po-section-title mb-2">DELIVERY & RECEIVING DETAILS</h6>
                    <div className="fw-bold fs-6 mb-1 text-indigo">${config.appName} Central Inventory Store</div>
                    <div className="small"><strong>Expected Delivery:</strong> {selectedPoDetails.expected_delivery_date ? new Date(selectedPoDetails.expected_delivery_date).toLocaleDateString() : 'Immediate Shipment'}</div>
                    <div className="small"><strong>Requisitioned By:</strong> {selectedPoDetails.creator_name || 'Store Administrator'}</div>
                    <div className="small"><strong>Payment Terms:</strong> Net 30 Days Upon GRN Verification</div>
                  </div>
                </Col>
              </Row>

              {/* Items Table Section Header Bar */}
              <div className="po-section-header-bar mb-2 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FaBoxes className="po-section-icon me-2" />
                  <span className="po-section-header-title">REQUISITIONED PHARMACEUTICAL ITEMS</span>
                </div>
                <span className="po-section-count-badge">{(selectedPoDetails.items || []).length} Item(s)</span>
              </div>
              <div className="table-responsive glassmorphic-subpanel mb-4">
                <Table bordered hover size="sm" className="po-doc-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }} className="text-center po-th-num">#</th>
                      <th style={{ width: '32%' }} className="po-th-name">Medicine Commercial Name</th>
                      <th style={{ width: '18%' }} className="po-th-brand">Manufacturer / Brand</th>
                      <th style={{ width: '11%' }} className="text-center po-th-qty">Requested</th>
                      <th style={{ width: '11%' }} className="text-center po-th-qty">Received</th>
                      <th style={{ width: '11%' }} className="text-center po-th-unit">Unit</th>
                      <th style={{ width: '12%' }} className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedPoDetails.items || []).map((item, idx) => {
                      const req = parseInt(item.requested_quantity, 10) || 0;
                      const rec = parseInt(item.total_received_quantity, 10) || 0;
                      const isFulfilled = rec >= req && req > 0;
                      return (
                        <tr key={idx}>
                          <td className="text-center po-num-col">{idx + 1}</td>
                          <td>
                            <div className="po-medicine-name">{item.medicine_name}</div>
                            {item.generic_name && <div className="po-generic-name">{item.generic_name}</div>}
                          </td>
                          <td className="po-manufacturer">{item.manufacturer || '—'}</td>
                          <td className="text-center po-qty">{req}</td>
                          <td className="text-center po-rec-qty">{rec}</td>
                          <td className="text-center po-unit">{item.unit_name ? item.unit_name.toUpperCase() : 'PIECE'}{item.unit_name?.toLowerCase() === 'box' ? 'ES' : 'S'}</td>
                          <td className="text-center">
                            {isFulfilled ? (
                              <span className="po-status-badge po-status-fulfilled">Fulfilled</span>
                            ) : rec > 0 ? (
                              <span className="po-status-badge po-status-partial">Partial ({rec}/{req})</span>
                            ) : (
                              <span className="po-status-badge po-status-pending">Pending</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Linked Goods Receipts & Batch Receiving History */}
              {selectedPoDetails.goods_receipts && selectedPoDetails.goods_receipts.length > 0 && (
                <div className="mb-4">
                  <div className="po-section-header-bar mb-2 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaTruckLoading className="po-section-icon me-2 text-success" />
                      <span className="po-section-header-title">GOODS RECEIPT (GRN) & SHIPMENT HISTORY</span>
                    </div>
                    <span className="po-section-count-badge">{selectedPoDetails.goods_receipts.length} Receipt(s)</span>
                  </div>
                  <div className="table-responsive glassmorphic-subpanel">
                    <Table bordered hover size="sm" className="po-doc-table po-grn-table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '18%' }} className="po-th-invoice">Invoice #</th>
                          <th style={{ width: '15%' }} className="po-th-date">Receipt Date</th>
                          <th style={{ width: '39%' }} className="po-th-items">Delivered Items & Batches</th>
                          <th style={{ width: '14%' }} className="text-end po-th-amount">Invoice Amount</th>
                          <th style={{ width: '14%' }} className="text-center po-th-status">Payment Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPoDetails.goods_receipts.map((grn, gIdx) => (
                          <tr key={gIdx}>
                            <td className="po-grn-invoice-cell">
                              <span className="po-grn-invoice-pill">{grn.invoice_number || `GRN-${grn.purchase_id}`}</span>
                            </td>
                            <td className="po-grn-date-cell">
                              <span className="po-grn-date-text">{new Date(grn.purchase_date).toLocaleDateString()}</span>
                            </td>
                            <td className="po-grn-items-cell">
                              {grn.items && grn.items.length > 0 ? (
                                <div className="d-flex flex-column gap-1">
                                  {grn.items.map((gi, giIdx) => (
                                    <div key={giIdx} className="po-grn-item-line d-flex justify-content-between align-items-center flex-wrap">
                                      <span className="po-grn-med-info">
                                        <strong className="po-grn-med-name">{gi.medicine_name}:</strong>{' '}
                                        <span className="po-grn-med-qty">{gi.quantity} {gi.selected_unit || 'box'}{gi.selected_unit?.toLowerCase() === 'box' ? 'es' : 's'}</span>
                                      </span>
                                      <span className="po-grn-batch-meta">
                                        Batch: <code className="po-grn-batch-num">{gi.batch_number}</code>
                                        {gi.expiry_date && <span className="po-grn-batch-exp"> (Exp: {new Date(gi.expiry_date).toLocaleDateString()})</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="po-grn-verified-label">Goods receipt verified</span>
                              )}
                            </td>
                            <td className="text-end po-grn-amount-cell">
                              <span className="po-grn-amount-val">{CURRENCY_SYMBOL}{parseFloat(grn.total_amount || 0).toFixed(2)}</span>
                            </td>
                            <td className="text-center po-grn-status-cell">
                              <span className={`po-status-badge po-status-${(grn.payment_status || 'pending').toLowerCase()}`}>
                                {grn.payment_status ? grn.payment_status.toUpperCase() : 'PENDING'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Notes & Special Instructions */}
              {selectedPoDetails.notes && (
                <div className="po-notes-card mb-4 p-3 rounded-3">
                  <strong className="d-block text-uppercase small po-notes-heading mb-1">Special Delivery Instructions & Terms:</strong>
                  <div className="small po-notes-content">{selectedPoDetails.notes}</div>
                </div>
              )}

              {/* Signatures & Footer Block */}
              <div className="po-doc-footer-signatures mt-4 pt-3 border-top d-flex justify-content-between align-items-end">
                <div className="text-muted extra-small">
                  <div>* This is an official computer-generated Purchase Order requisition.</div>
                  <div>* Please quote PO Number <strong>{selectedPoDetails.po_number}</strong> on all delivery slips and tax invoices.</div>
                </div>
                <div className="text-center po-signature-box">
                  <div className="po-signature-line mb-1"></div>
                  <div className="fw-bold small">Authorized Purchase Officer</div>
                  <div className="extra-small text-muted">${config.appName} Pharmacy Logistics</div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between no-print">
          <div className="d-flex gap-2">
            <Button variant="outline-success" onClick={() => handleWhatsAppShare(selectedPoDetails)}>
              <FaWhatsapp className="me-1" /> Quick Send WhatsApp
            </Button>
            <Button variant="primary" onClick={handlePrintPo}>
              <FaPrint className="me-1" /> Print / Save PDF
            </Button>
          </div>
          <Button variant="secondary" onClick={() => setShowPoDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 3: GOODS RECEIPT VERIFICATION (GRN) */}
      <Modal show={showGrnModal} onHide={() => setShowGrnModal(false)} size="xl" centered className="purchase-modal grn-modal">
        <Modal.Header closeButton className="grn-modal-header px-4 py-3">
          <Modal.Title className="fw-bold grn-modal-title d-flex align-items-center">
            <div className="grn-title-icon-wrap me-2">
              <FaTruckLoading className="text-primary" />
            </div>
            <span>Verify Goods Receipt (GRN)</span>
            <span className="grn-po-highlight-badge ms-2">{grnPo?.po_number}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="grn-modal-body p-4">
          {/* Top Inspection & Invoice Control Banner */}
          <div className="grn-top-banner mb-3 p-3 rounded-3">
            <Row className="align-items-center gy-3">
              <Col lg={7} md={6}>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  <div className="grn-meta-chip">
                    <span className="grn-meta-chip-label">PO Ref:</span>
                    <span className="grn-meta-chip-value">{grnPo?.po_number}</span>
                  </div>
                  <div className="grn-meta-chip">
                    <span className="grn-meta-chip-label">Supplier:</span>
                    <span className="grn-meta-chip-value text-truncate" style={{ maxWidth: '240px' }} title={grnPo?.supplier_name}>
                      {grnPo?.supplier_name || 'N/A'}
                    </span>
                  </div>
                  <div className="grn-meta-chip">
                    <span className="grn-meta-chip-label">Lines:</span>
                    <span className="grn-meta-chip-value">{grnForm.items.length} item(s)</span>
                  </div>
                </div>
                <div className="grn-banner-hint d-flex align-items-center">
                  <FaInfoCircle className="me-2 text-info flex-shrink-0" />
                  <span>Inspect delivered packages against original order. Enter actual delivered batch numbers, expiry dates, and unit quantities.</span>
                </div>
              </Col>
              <Col lg={5} md={6}>
                <div className="grn-invoice-input-card p-2 rounded-2">
                  <label className="grn-invoice-label mb-1 d-flex align-items-center">
                    <FaFileInvoiceDollar className="me-2 text-primary" />
                    <span>Supplier Invoice / Bill Number</span>
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <InputGroup size="sm" className="grn-invoice-group">
                    <Form.Control 
                      type="text"
                      value={grnForm.invoice_number}
                      onChange={(e) => setGrnForm({ ...grnForm, invoice_number: e.target.value })}
                      placeholder="e.g. INV-2026-9901"
                      className="grn-input-field grn-invoice-field fw-bold"
                    />
                  </InputGroup>
                </div>
              </Col>
            </Row>
          </div>

          {/* High-Precision Receiving Table */}
          <div className="table-responsive grn-table-wrapper mb-3">
            <Table align-middle className="grn-table mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: '190px' }}>Medicine</th>
                  <th style={{ minWidth: '175px' }}>Order Progress</th>
                  <th style={{ minWidth: '100px' }} className="text-center">Rec. Now</th>
                  <th style={{ minWidth: '145px' }}>Receiving Unit</th>
                  <th style={{ minWidth: '145px' }}>Batch #</th>
                  <th style={{ minWidth: '145px' }}>Expiry Date</th>
                  <th style={{ minWidth: '120px' }}>Base Rate</th>
                  <th style={{ minWidth: '85px' }} className="text-center">GST Slab</th>
                  <th style={{ minWidth: '100px' }} className="text-center">Tax ({CURRENCY_SYMBOL})</th>
                  <th style={{ minWidth: '120px' }}>Selling Price</th>
                  <th style={{ minWidth: '85px' }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {grnForm.items.map((item, idx) => {
                  const reqQty = parseInt(item.requested_quantity, 10) || 0;
                  const alreadyRec = parseInt(item.total_received_quantity, 10) || 0;
                  const currentRec = parseInt(item.received_quantity, 10) || 0;
                  const currentFactor = parseInt(item.conversion_factor, 10) || 1;

                  // Total base units for this PO line
                  const totalBaseOrdered = item.total_ordered_base_units !== undefined
                    ? item.total_ordered_base_units
                    : (item.po_requested_quantity * (item.po_conversion_factor || 1));

                  // Base units already received prior to this GRN session
                  const totalBaseAlreadyRec = item.total_received_base_units !== undefined
                    ? item.total_received_base_units
                    : (alreadyRec * currentFactor);

                  // Base units being received in this session across all rows for this same PO item
                  const totalReceivingThisSessionBase = grnForm.items
                    .filter(other => (item.po_item_id && other.po_item_id === item.po_item_id) || (!item.po_item_id && other.medicine_id === item.medicine_id))
                    .reduce((sum, other) => sum + ((parseInt(other.received_quantity, 10) || 0) * (parseInt(other.conversion_factor, 10) || 1)), 0);

                  const netRemainingBase = Math.max(0, totalBaseOrdered - totalBaseAlreadyRec - totalReceivingThisSessionBase);
                  const remQtyInCurrentUnit = currentFactor > 0 ? Math.ceil(netRemainingBase / currentFactor) : netRemainingBase;

                  const totalCumulativeBase = totalBaseAlreadyRec + totalReceivingThisSessionBase;
                  const isFulfilled = totalCumulativeBase >= totalBaseOrdered && totalBaseOrdered > 0;

                  const itemBase = currentRec * (parseFloat(item.purchase_price) || 0);
                  const itemTax = (itemBase * (parseFloat(item.gst_rate !== undefined ? item.gst_rate : 12))) / 100;

                  return (
                    <tr key={idx} className={`grn-row ${isFulfilled ? 'grn-row-fulfilled' : 'grn-row-shortage'}`}>
                      {/* Medicine Info */}
                      <td>
                        <div className="grn-med-cell">
                          <div className="grn-med-title-row d-flex align-items-center justify-content-between">
                            <span className="grn-med-name">{item.medicine_name}</span>
                            {item.is_split && (
                              <span className="grn-split-badge" title="Additional batch for same medicine">+Split</span>
                            )}
                          </div>
                          <div className="grn-med-tags mt-1 d-flex align-items-center gap-1 flex-wrap">
                            <span className="grn-tag-hsn">HSN {item.hsn_code || '3004'}</span>
                            {item.base_unit_name && currentFactor > 1 && (
                              <span className="grn-tag-base">1 {item.unit_name} = {currentFactor} {item.base_unit_name}</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Order Progress & Balance */}
                      <td>
                        <div className="grn-progress-panel">
                          <div className="grn-prog-line d-flex justify-content-between align-items-center">
                            <span className="grn-prog-label">PO Order:</span>
                            <span className="grn-prog-main-val">
                              {item.po_requested_quantity} {item.po_unit_name}{item.po_requested_quantity > 1 ? (item.po_unit_name.toLowerCase() === 'box' ? 'es' : 's') : ''}
                            </span>
                          </div>

                          {item.po_unit_name && item.unit_name.toLowerCase() !== item.po_unit_name.toLowerCase() && (
                            <div className="grn-prog-converted-sub text-end">
                              (= {reqQty} {item.unit_name}{reqQty > 1 ? (item.unit_name.toLowerCase() === 'box' ? 'es' : 's') : ''})
                            </div>
                          )}

                          {alreadyRec > 0 && (
                            <div className="grn-prog-line d-flex justify-content-between align-items-center grn-prog-prev-line">
                              <span className="grn-prog-label">Prev Rec:</span>
                              <span className="grn-prog-sec-val">
                                {alreadyRec} {item.unit_name}
                              </span>
                            </div>
                          )}

                          <div className="grn-prog-status-row mt-1">
                            {isFulfilled ? (
                              <span className="grn-status-pill grn-status-fulfilled">
                                <FaCheckCircle className="me-1" /> Fulfilled
                              </span>
                            ) : (
                              <span className="grn-status-pill grn-status-pending">
                                <FaClock className="me-1" /> {remQtyInCurrentUnit} {item.unit_name}{remQtyInCurrentUnit > 1 ? (item.unit_name.toLowerCase() === 'box' ? 'es' : 's') : ''} left
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Delivered Qty (Rec. Now) */}
                      <td>
                        <Form.Control 
                          type="number"
                          min="0"
                          size="sm"
                          value={item.received_quantity}
                          onChange={(e) => handleGrnItemChange(idx, 'received_quantity', parseInt(e.target.value, 10) || 0)}
                          className="grn-input-field grn-qty-field text-center fw-bold"
                        />
                      </td>

                      {/* Unit Dropdown */}
                      <td>
                        <Form.Select 
                          size="sm"
                          value={item.unit_name}
                          onChange={(e) => handleGrnItemChange(idx, 'unit_name', e.target.value)}
                          className="grn-select-field grn-unit-select"
                        >
                          <option value="box">Box ({item.box_conversion || 100})</option>
                          <option value="strip">Strip ({item.strip_conversion || 10})</option>
                          <option value="piece">Piece ({item.base_unit_name || 'piece'})</option>
                        </Form.Select>
                      </td>

                      {/* Batch Number */}
                      <td>
                        <Form.Control 
                          type="text"
                          size="sm"
                          placeholder="Batch #"
                          value={item.batch_number}
                          onChange={(e) => handleGrnItemChange(idx, 'batch_number', e.target.value)}
                          className="grn-input-field grn-batch-field"
                        />
                      </td>

                      {/* Expiry Date */}
                      <td>
                        <Form.Control 
                          type="date"
                          size="sm"
                          className="grn-input-field grn-expiry-field"
                          value={item.expiry_date}
                          onChange={(e) => handleGrnItemChange(idx, 'expiry_date', e.target.value)}
                        />
                      </td>

                      {/* Base Purchase Rate */}
                      <td>
                        <InputGroup size="sm" className="grn-currency-group">
                          <InputGroup.Text className="grn-curr-symbol">{CURRENCY_SYMBOL}</InputGroup.Text>
                          <Form.Control 
                            type="number"
                            step="0.01"
                            value={item.purchase_price}
                            onChange={(e) => handleGrnItemChange(idx, 'purchase_price', parseFloat(e.target.value) || 0)}
                            className="grn-input-field grn-price-field"
                          />
                        </InputGroup>
                      </td>

                      {/* GST Slab Dropdown */}
                      <td>
                        <Form.Select
                          size="sm"
                          value={item.gst_rate !== undefined ? item.gst_rate : 12}
                          onChange={(e) => handleGrnItemChange(idx, 'gst_rate', e.target.value)}
                          className="grn-select-field grn-gst-select text-center"
                        >
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                        </Form.Select>
                      </td>

                      {/* Computed GST Tax Amount */}
                      <td className="text-center">
                        <div className="grn-tax-amount-badge">
                          {CURRENCY_SYMBOL}{itemTax.toFixed(2)}
                        </div>
                      </td>

                      {/* Selling Price */}
                      <td>
                        <InputGroup size="sm" className="grn-currency-group">
                          <InputGroup.Text className="grn-curr-symbol">{CURRENCY_SYMBOL}</InputGroup.Text>
                          <Form.Control 
                            type="number"
                            step="0.01"
                            value={item.selling_price}
                            onChange={(e) => handleGrnItemChange(idx, 'selling_price', parseFloat(e.target.value) || 0)}
                            className="grn-input-field grn-price-field"
                          />
                        </InputGroup>
                      </td>

                      {/* Actions */}
                      <td className="text-center">
                        {item.is_split ? (
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="grn-btn-action grn-btn-delete"
                            title="Remove this split batch line"
                            onClick={() => handleRemoveSplitBatch(idx)}
                          >
                            <FaTrash size={12} />
                          </Button>
                        ) : (
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="grn-btn-action grn-btn-add-batch"
                            title="Add another batch for this medicine"
                            onClick={() => handleAddSplitBatch(idx)}
                          >
                            <FaPlus size={10} className="me-1" />
                            <span>Batch</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Executive GRN Financial Summary Card */}
          {(() => {
            const grnSubtotal = grnForm.items.reduce((sum, i) => sum + ((parseInt(i.received_quantity, 10) || 0) * (parseFloat(i.purchase_price) || 0)), 0);
            const grnTotalTax = grnForm.items.reduce((sum, i) => {
              const base = (parseInt(i.received_quantity, 10) || 0) * (parseFloat(i.purchase_price) || 0);
              const rate = parseFloat(i.gst_rate !== undefined ? i.gst_rate : 12);
              return sum + (base * rate) / 100;
            }, 0);
            const grnNetTotal = grnSubtotal + grnTotalTax;

            return (
              <div className="grn-summary-panel p-3 rounded-3">
                <Row className="align-items-center gy-3">
                  <Col xs={12} md={4}>
                    <div className="grn-summary-title">Base Taxable Subtotal</div>
                    <div className="grn-summary-digit grn-subtotal-val">
                      {CURRENCY_SYMBOL}{grnSubtotal.toFixed(2)}
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="grn-summary-title">Input Tax Credit (GST)</div>
                    <div className="grn-summary-digit grn-tax-val">
                      {CURRENCY_SYMBOL}{grnTotalTax.toFixed(2)}
                    </div>
                    <div className="grn-summary-cgst-sgst">
                      Central CGST: {CURRENCY_SYMBOL}{(grnTotalTax / 2).toFixed(2)} • State SGST: {CURRENCY_SYMBOL}{(grnTotalTax / 2).toFixed(2)}
                    </div>
                  </Col>
                  <Col xs={12} md={4} className="text-md-end">
                    <div className="grn-summary-title">Net Total Payable to Supplier</div>
                    <div className="grn-summary-digit grn-net-val">
                      {CURRENCY_SYMBOL}{grnNetTotal.toFixed(2)}
                    </div>
                  </Col>
                </Row>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="grn-modal-footer px-4 py-3">
          <Button variant="secondary" className="grn-footer-btn-cancel px-3" onClick={() => setShowGrnModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            disabled={submittingGrn}
            onClick={handleSubmitGrn}
            className="grn-footer-btn-submit px-4 py-2 fw-bold"
          >
            <FaCheckCircle className="me-2" /> Verify Goods Receipt & Update Active Inventory
          </Button>
        </Modal.Footer>
      </Modal>

      {/* RECORD PAYMENT MODAL */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered className="po-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 payment-modal-title">
            <FaHandHoldingUsd /> Record Supplier Payment
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitPayment}>
          <Modal.Body>
            {selectedPurchaseForPayment && (
              <div className="p-3 mb-4 rounded-3 payment-summary-card">
                <Row className="gy-3">
                  <Col xs={6}>
                    <div className="payment-info-label">Invoice Number</div>
                    <div className="payment-info-value payment-invoice-no">{selectedPurchaseForPayment.invoice_number}</div>
                  </Col>
                  <Col xs={6}>
                    <div className="payment-info-label">Supplier</div>
                    <div className="payment-info-value">{selectedPurchaseForPayment.supplier_name || 'N/A'}</div>
                  </Col>
                  <Col xs={4}>
                    <div className="payment-info-label">Total Invoice</div>
                    <div className="payment-info-value">{CURRENCY_SYMBOL}{parseFloat(selectedPurchaseForPayment.total_amount || 0).toFixed(2)}</div>
                  </Col>
                  <Col xs={4}>
                    <div className="payment-info-label">Total Paid</div>
                    <div className="payment-info-value text-success-bright">{CURRENCY_SYMBOL}{parseFloat(selectedPurchaseForPayment.total_paid || 0).toFixed(2)}</div>
                  </Col>
                  <Col xs={4}>
                    <div className="payment-info-label">Remaining Due</div>
                    <div className="payment-info-value text-danger-bright">
                      {CURRENCY_SYMBOL}{(selectedPurchaseForPayment.remaining_due !== undefined ? parseFloat(selectedPurchaseForPayment.remaining_due) : (parseFloat(selectedPurchaseForPayment.total_amount || 0) - parseFloat(selectedPurchaseForPayment.total_paid || 0))).toFixed(2)}
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Previous Payments Log within Record Payment Dialog */}
            {selectedPurchaseForPayment?.payments && selectedPurchaseForPayment.payments.length > 0 && (
              <div className="mb-4">
                <div className="payment-info-label mb-2">Previous Payments Recorded ({selectedPurchaseForPayment.payments.length})</div>
                <div className="table-responsive glassmorphic-subpanel" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  <Table hover size="sm" className="mb-0 payment-history-table" style={{ fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Ref #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPurchaseForPayment.payments.map((p) => (
                        <tr key={p.payment_id}>
                          <td>{new Date(p.paid_at).toLocaleDateString()}</td>
                          <td className="fw-bold text-success-bright">{CURRENCY_SYMBOL}{parseFloat(p.amount).toFixed(2)}</td>
                          <td>{renderPaymentMethodBadge(p.payment_method)}</td>
                          <td className="small payment-notes-text">{p.transaction_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="payment-form-label">Payment Amount ({CURRENCY_SYMBOL})</Form.Label>
              <InputGroup>
                <InputGroup.Text className="payment-input-symbol">{CURRENCY_SYMBOL}</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="payment-form-label">Payment Method</Form.Label>
              <Form.Select
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI / GPay / PhonePe</option>
                <option value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</option>
                <option value="card">Credit / Debit Card</option>
                <option value="cheque">Cheque</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="payment-form-label">Transaction / Reference ID (Optional)</Form.Label>
              <Form.Control
                type="text"
                value={paymentForm.transaction_id}
                onChange={(e) => setPaymentForm({ ...paymentForm, transaction_id: e.target.value })}
                placeholder="E.g. UPI Ref # or UTR Number"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="payment-form-label">Notes / Remarks (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="E.g. Partial payment against GRN verification"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={submittingPayment}>
              {submittingPayment ? 'Saving...' : 'Submit Payment'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL: INVOICE DETAILS & PAYMENT HISTORY */}
      <Modal show={showInvoiceDetailsModal} onHide={() => setShowInvoiceDetailsModal(false)} size="lg" centered className="purchase-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 payment-modal-title">
            <FaReceipt /> Invoice #{invoiceDetails?.invoice_number} Details & Payment History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingInvoiceDetails ? (
            <ProgressDialog show={true} message="Loading Invoice & Payment Details..." />
          ) : invoiceDetails ? (
            <div>
              {/* Summary Card */}
              <div className="p-3 mb-4 rounded-3 payment-summary-card">
                <Row className="gy-3">
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Invoice Number</div>
                    <div className="payment-info-value payment-invoice-no">{invoiceDetails.invoice_number}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Date</div>
                    <div className="payment-info-value">{new Date(invoiceDetails.purchase_date).toLocaleDateString()}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Supplier</div>
                    <div className="payment-info-value">{invoiceDetails.supplier?.name || 'N/A'}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Status</div>
                    <div>
                      <Badge bg={invoiceDetails.payment_status === 'paid' ? 'success' : invoiceDetails.payment_status === 'partially_paid' ? 'warning' : 'danger'}>
                        {invoiceDetails.payment_status?.toUpperCase()}
                      </Badge>
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Base Taxable Subtotal</div>
                    <div className="payment-info-value">{CURRENCY_SYMBOL}{parseFloat(invoiceDetails.subtotal_amount || (invoiceDetails.total_amount - (invoiceDetails.total_tax_amount || 0))).toFixed(2)}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Input Tax Credit (GST)</div>
                    <div className="payment-info-value text-info">
                      {CURRENCY_SYMBOL}{parseFloat(invoiceDetails.total_tax_amount || 0).toFixed(2)}
                      <span className="small text-muted ms-1" style={{ fontSize: '0.75rem' }}>(CGST: {CURRENCY_SYMBOL}{(parseFloat(invoiceDetails.total_tax_amount || 0)/2).toFixed(2)})</span>
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Net Invoice Amount</div>
                    <div className="payment-info-value fw-bold">{CURRENCY_SYMBOL}{parseFloat(invoiceDetails.total_amount || 0).toFixed(2)}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="payment-info-label">Status / Remaining Due</div>
                    <div>
                      <Badge bg={invoiceDetails.payment_status === 'paid' ? 'success' : invoiceDetails.payment_status === 'partially_paid' ? 'warning' : 'danger'} className="me-2">
                        {invoiceDetails.payment_status?.toUpperCase()}
                      </Badge>
                      <span className="text-danger-bright fw-semibold small">Due: {CURRENCY_SYMBOL}{parseFloat(invoiceDetails.remaining_due || 0).toFixed(2)}</span>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Items Purchased Section */}
              <h6 className="fw-bold mb-2 payment-section-header d-flex align-items-center gap-2">
                <FaBoxes /> Items Purchased in GRN
              </h6>
              <div className="table-responsive glassmorphic-subpanel mb-4">
                <Table hover align-middle size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Medicine Name</th>
                      <th>Batch #</th>
                      <th>Qty</th>
                      <th>Base Rate</th>
                      <th>GST Slab</th>
                      <th>GST Tax</th>
                      <th>Selling Price</th>
                      <th>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoiceDetails.items || []).map((item, idx) => (
                      <tr key={idx}>
                        <td className="fw-bold">{item.medicine_name}</td>
                        <td><Badge className="batch-badge">{item.batch_number || 'N/A'}</Badge></td>
                        <td>{item.quantity} {item.selected_unit}s</td>
                        <td>{CURRENCY_SYMBOL}{parseFloat(item.purchase_price || 0).toFixed(2)}</td>
                        <td>
                          <Badge bg="info" className="text-dark">
                            {parseFloat(item.tax_rate || item.gst_rate || 0)}%
                          </Badge>
                        </td>
                        <td className="text-info fw-semibold">
                          {CURRENCY_SYMBOL}{parseFloat(item.tax_amount || 0).toFixed(2)}
                        </td>
                        <td>{CURRENCY_SYMBOL}{parseFloat(item.selling_price || 0).toFixed(2)}</td>
                        <td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Payment History Log Section */}
              <h6 className="fw-bold mb-2 payment-section-header d-flex align-items-center gap-2">
                <FaHandHoldingUsd /> Payment Transactions History
              </h6>
              {(!invoiceDetails.payments || invoiceDetails.payments.length === 0) ? (
                <div className="p-3 text-center payment-no-data-msg glassmorphic-subpanel rounded-3">
                  No payment transactions recorded for this invoice yet.
                </div>
              ) : (
                <div className="table-responsive glassmorphic-subpanel">
                  <Table hover align-middle size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Date & Time</th>
                        <th>Amount Paid</th>
                        <th>Payment Method</th>
                        <th>Transaction / Ref ID</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceDetails.payments.map((pmt) => (
                        <tr key={pmt.payment_id}>
                          <td>{new Date(pmt.paid_at).toLocaleString()}</td>
                          <td className="fw-bold text-success-bright">{CURRENCY_SYMBOL}{parseFloat(pmt.amount || 0).toFixed(2)}</td>
                          <td>{renderPaymentMethodBadge(pmt.payment_method)}</td>
                          <td className="font-monospace payment-ref-id">{pmt.transaction_id || '-'}</td>
                          <td className="small payment-notes-text">{pmt.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoiceDetailsModal(false)}>
            Close
          </Button>
          {invoiceDetails && parseFloat(invoiceDetails.remaining_due || 0) > 0 && (
            <Button 
              variant="success" 
              onClick={() => {
                setShowInvoiceDetailsModal(false);
                handleOpenPaymentModal(invoiceDetails);
              }}
            >
              <FaHandHoldingUsd className="me-1" /> Record New Payment
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PurchaseManagement;
